import React from "react";
import { GhlCall, CallsAttachment } from "../types";

interface CallsListCardProps {
  data: CallsAttachment;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const badgeStyle = (missed: boolean): React.CSSProperties =>
  missed
    ? {
        background: "color-mix(in srgb, var(--color-danger) 16%, var(--color-surface))",
        color: "var(--color-danger)",
      }
    : {
        background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
        color: "var(--color-primary)",
      };

// Same fix as ContactsListCard.tsx: bg-theme-secondary/20 resolves to
// `background-color: var(--color-secondary)/20`, which Tailwind can't turn
// into a real alpha blend for a bare var() color — it silently renders with
// no background. Inline color-mix() instead (already used for badgeStyle above).
const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

const headerIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
};

const statusLabel = (status: string) =>
  status
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ") || "Unknown";

const formatDate = (iso: string | null) => {
  if (!iso) return "Unknown time";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown time";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDuration = (seconds: number | null) => {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/** Read-only GHL calls list (output_calls tool, AI Receptionist) — same
 *  visual language as ContactsListCard: no editing, just a live GHL browse. */
export const CallsListCard: React.FC<CallsListCardProps> = ({ data }) => {
  const { calls, truncated } = data;
  const missedCount = calls.filter((c) => c.missed).length;

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between border-b border-theme-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={headerIconStyle}>
            <span className="material-icons-outlined text-[18px] text-theme-primary">call</span>
          </span>
          <p className="text-[13px] font-bold text-theme-fg">
            {calls.length} {calls.length === 1 ? "Call" : "Calls"}
          </p>
        </div>
        {missedCount > 0 && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold"
            style={badgeStyle(true)}
          >
            {missedCount} missed
          </span>
        )}
      </div>

      <div className="flex max-h-[360px] flex-col gap-1.5 overflow-y-auto p-3">
        {calls.length === 0 && (
          <p className="px-2 py-6 text-center text-[12.5px] text-theme-fg-muted">No calls found.</p>
        )}
        {calls.map((c: GhlCall, i: number) => {
          const duration = formatDuration(c.duration_seconds);
          return (
            <div
              // conversation_id alone isn't unique — a conversation can have
              // multiple call messages (confirmed live: same conversation_id,
              // different date/duration) — index as tiebreaker guarantees uniqueness.
              key={`${c.conversation_id ?? "unknown"}-${c.date ?? i}-${i}`}
              className="flex items-center gap-3 rounded-xl border border-theme-border px-3 py-2"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold" style={avatarTint(i)}>
                {initials(c.contact_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-theme-fg">
                  {c.contact_name || "(no name)"}
                  {c.phone && <span className="font-medium text-theme-fg-muted">&middot; {c.phone}</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-3 text-[11.5px] text-theme-fg-muted">
                  <span className="inline-flex items-center gap-1">
                    <span className="material-icons-outlined text-[13px]">
                      {c.direction === "outbound" ? "call_made" : c.direction === "inbound" ? "call_received" : "call"}
                    </span>
                    {formatDate(c.date)}
                  </span>
                  {duration && <span>{duration}</span>}
                </div>
              </div>
              <span
                className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
                style={badgeStyle(c.missed)}
              >
                {statusLabel(c.status)}
              </span>
            </div>
          );
        })}
      </div>

      {truncated && (
        <div className="border-t border-theme-border px-4 py-2 text-[11px] text-theme-fg-muted">
          This list may be incomplete for the requested range — try narrowing the dates for a fuller picture.
        </div>
      )}
    </div>
  );
};
